import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "@/styles/app.css";
// Side-effect import: initializes i18next + applies the persisted language
// (localStorage "sm_lang") BEFORE the router renders, so the first round of
// query keys and catalog fetchers already speak the right locale.
import "@/lib/i18n";

import { routeTree } from "./routeTree.gen";
import { NotFound } from "@/components/NotFound";
import { ErrorScreen } from "@/components/ErrorScreen";
import { dropStaticMeta } from "@/lib/seo";
import { BrandProvider, applyBrandTheme, loadBrandTheme } from "@/brand";

// Single QueryClient — 30s default stale time so live REST polls don't
// spam the gateway when WS streams the same data. WS handlers invalidate
// via queryClient.invalidateQueries on impactful events.
//
// Retry policy is differentiated by status: 4xx never retried (except 429
// up to 4×), 5xx up to 3×, network/unknown up to 2×; exponential backoff
// (250/500ms base, capped 4s) with full jitter so post-recovery traffic
// doesn't thunder.
const isTransient = (status: number | undefined) =>
  status === 429 || (status !== undefined && status >= 500 && status < 600);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (count, err) => {
        const status = (err as { status?: number }).status;
        if (status === undefined) return count < 2;
        if (status >= 400 && status < 500 && status !== 429) return false;
        if (status === 429) return count < 4;
        if (status >= 500) return count < 3;
        return false;
      },
      retryDelay: (count, err) => {
        const status = (err as { status?: number }).status;
        const base = isTransient(status) ? 500 : 250;
        const expo = Math.min(base * 2 ** count, 4_000);
        return expo / 2 + Math.random() * (expo / 2);
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: ({ error, reset }) => <ErrorScreen error={error} reset={reset} />,
  // The app shell scrolls an inner pane, not the window (see AppShell). Reset
  // that pane to the top on each fresh navigation — TanStack's default only
  // scrolls the window, which never moves here. Back/forward still restores the
  // pane's saved position via its data-scroll-restoration-id.
  scrollToTopSelectors: ['[data-scroll-restoration-id="app-scroll"]'],
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Stale-deploy recovery. Every route is a content-hashed lazy chunk
// (autoCodeSplitting), and chunks are served `immutable`. When a new build
// ships, the old chunks 404, so the next route import — or even an `intent`
// preload on hover/touch — rejects ("Importing a module script failed" on
// iOS, "Failed to fetch dynamically imported module" on Chrome) and drops the
// whole app into the error boundary. The cure is a full reload: index.html is
// served no-cache, so it comes back pointing at the new build's chunk names.
// Vite fires `vite:preloadError` for exactly these failed dynamic imports.
// Reload once per occurrence, guarded against a reload loop in case a chunk is
// genuinely broken rather than merely stale — then we fall through to the
// error boundary (ErrorScreen offers a manual Reload) instead of looping.
const CHUNK_RELOAD_KEY = "sm:chunk-reload-at";
window.addEventListener("vite:preloadError", (event) => {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    /* sessionStorage unavailable (private mode) — proceed to reload anyway */
  }
  if (Date.now() - last < 10_000) return; // just reloaded for this → let it surface
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  event.preventDefault(); // we're handling it — suppress Vite's rethrow
  window.location.reload();
});

// Retire index.html's no-JavaScript SEO/OG fallback before the router
// renders its own tags (SM-381). Both sets in <head> at once would leave
// two og:title tags per page with the generic one first — see
// dropStaticMeta in src/lib/seo.ts.
dropStaticMeta();

// Resolve the brand BEFORE the first render, so the app never paints one
// brand's colours or marks and then swaps. The loader is async by contract
// (a later ticket moves the source behind an API); against today's bundled
// registry it settles in a microtask, and it degrades to SmartMarket rather
// than rejecting — so there is no loading state for a consumer to handle.
void loadBrandTheme().then((brand) => {
  applyBrandTheme(brand);

  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
      <BrandProvider theme={brand}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        </QueryClientProvider>
      </BrandProvider>
    </React.StrictMode>,
  );
});
